import * as React from 'react';
import { Droppable, DragDropContext, type OnDragEndResponder } from '@hello-pangea/dnd';
import { notify } from 'services/notification';
import { useTranslation } from 'hooks';
import { useForwardTripState, useBackwardTripState } from 'pages/Settings/transport-routes/hooks';
import type { TripDirection, TripStopLocation } from 'contracts/transport-routes';
import { Card, CardContent } from 'components/ui/card';
import { Table, TableBody, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { ConfirmationDialog, type ConfirmationDialogRef } from 'components/ui/confirmation-dialog';
import { cn } from 'lib/utils';

import { TripStopListItem } from './trip-stop-list-item';
import { translations } from './translations';
import { generateLocationName } from './utils';

type Ref = {
  getStopLocations: () => TripStopLocation[];
  addStopLocation: (stop: TripStopLocation) => void;
  setStopLocations: (stops: TripStopLocation[]) => void;
};

type Props = React.HTMLAttributes<HTMLDivElement> & {
  tripDirection: TripDirection;
}

export type TripStopsListRef = Ref;

function reorder<T>(list: T[], startIndex: number, endIndex: number): T[] {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
}

export const TripStopsList = React.forwardRef<Ref, Props>(({ tripDirection, className, ...props }, ref) => {
  const { getTranslation: t } = useTranslation(translations);

  const { updateForwardTripStopLocations } = useForwardTripState();
  const { updateBackwardTripStopLocations } = useBackwardTripState();

  const removeStopLocationConfirmationDialogRef = React.useRef<ConfirmationDialogRef>(null);

  const [stopLocations, setStopLocations] = React.useState<TripStopLocation[]>([]);

  const onDragEnd: OnDragEndResponder = React.useCallback((result) => {
    const { destination, source, type } = result;

    if (!destination) {
      return;
    }

    // if dropped in the same position
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    if (type === 'list') {
      const items = reorder(
        stopLocations,
        source.index,
        destination.index,
      ).map((item, index) => ({ ...item, order: index }));

      setStopLocations(items);
    }
  }, [stopLocations]);

  const onAddStopLocation = React.useCallback((stopLocationToAdd: TripStopLocation) => {
    const locationAlreadyExists = stopLocations.some((location) => {
      return location.lat === stopLocationToAdd.lat && location.lng === stopLocationToAdd.lng;
    });

    if (locationAlreadyExists) {
      return notify('error', t('stopAlreadyExistsMessage'));
    }

    const newStopLocations = [...stopLocations, {
      ...stopLocationToAdd,
      order: stopLocations.length,
    }];

    setStopLocations(newStopLocations);
  }, [stopLocations, t]);

  const onRemoveStopLocation = React.useCallback(async (stopLocationToRemove: TripStopLocation) => {
    const conformationMessage = t('removeConfirmationMessage')
      .replace('{{locationName}}', generateLocationName(stopLocationToRemove));

    const dialogResponse = await removeStopLocationConfirmationDialogRef.current?.openDialog({
      message: conformationMessage,
    });

    if (dialogResponse !== 'confirmed') return;

    const newLocations = stopLocations
      .filter((location) => location.id! !== stopLocationToRemove.id!)
      .map((location, index) => ({ ...location, order: index }));

    setStopLocations(newLocations);
  }, [stopLocations, t]);

  const onReceiveTripStopLocations = React.useCallback((stops: TripStopLocation[]) => {
    setStopLocations(stops);
  }, []);

  const onGetStopLocations = React.useCallback(() => {
    return stopLocations.map(stopLocation => {
      const { id, ...stopLocationData } = stopLocation;

      if (typeof id === 'string') {
        return stopLocationData;
      } else {
        return stopLocation;
      }
    });
  }, [stopLocations]);

  React.useImperativeHandle(ref, () => ({
    getStopLocations: onGetStopLocations,
    addStopLocation: onAddStopLocation,
    setStopLocations: onReceiveTripStopLocations,
  }), [
    onGetStopLocations,
    onAddStopLocation,
    onReceiveTripStopLocations,
  ]);

  const updateTripRoutesState = React.useCallback(() => {
    if (tripDirection === 'forward') {
      updateForwardTripStopLocations(onGetStopLocations());
    } else {
      updateBackwardTripStopLocations(onGetStopLocations());
    }
  }, [
    stopLocations,
    tripDirection,
    updateForwardTripStopLocations,
    updateBackwardTripStopLocations,
    onGetStopLocations,
  ]);

  React.useEffect(() => {
    updateTripRoutesState();
  }, [updateTripRoutesState]);

  if (!stopLocations.length) {
    return null;
  }

  return (
    <Card {...props} className={cn('w-full', className)}>
      <CardContent className={'pt-6'}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className={'w-14'}></TableHead>
                <TableHead className={'w-20'}>{t('type')}</TableHead>
                <TableHead>{t('value')}</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <Droppable droppableId={'stop-locations-list'} type={'list'} direction={'vertical'}>
              {(provided) => (
                <TableBody
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {stopLocations.map((location, index) => (
                    <TripStopListItem
                      key={location.id!}
                      index={index}
                      location={location}
                      onClickRemoveStopLocation={onRemoveStopLocation}
                    />
                  ))}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </Table>
        </DragDropContext>
      </CardContent>
      <ConfirmationDialog
        ref={removeStopLocationConfirmationDialogRef}
      />
    </Card>
  );
});
TripStopsList.displayName = 'TripStopsList';